/**
 * SAFECARE Dynamic Events System (Corrected Year-Aware Architecture)
 * Defines major external shocks assuming exponential decay from a trigger year.
 */

export const SIMULATION_EVENTS = [
    // --- NEGATIVE SHOCKS ---
    {
        id: 'covid25_pandemic',
        type: 'negative',
        label: "🦠 COVID-25 Pandemic",
        description: "Novel respiratory virus overwhelms hospitals (Peak + Recovery).",
        duration: 3,
        decayHalfLife: 1.5,
        defaultSeverity: 1.25,
        impact: {
            revenue: 0.88,        // Peak 12% drop
            hospital: 1.45,       // Peak 45% surge
            physician: 1.25,
            drugs: 1.15,
            admin: 1.40,
            gdp_growth: -0.03     // Additive (not multiplicative)
        }
    },
    {
        id: 'pharma_bankruptcy',
        type: 'negative',
        label: "💊 Major Pharma Bankruptcy",
        description: "Generic manufacturer collapse due to price controls.",
        duration: 5,
        decayHalfLife: 2.0,
        defaultSeverity: 1.0,
        impact: {
            drugs: 1.65,          // +65% peak
            innovation_penalty: 1.20 // Special flag
        }
    },
    {
        id: 'rural_collapse',
        type: 'negative',
        label: "🏥 Rural Hospital Collapse",
        description: "20% rural closures overwhelm urban systems.",
        duration: 10,
        decayHalfLife: 5.0, // Slow recovery
        defaultSeverity: 1.0,
        impact: {
            hospital: 1.25,       // Urban surge
            // ruralUplift handled via config override if needed, but multiplier is easier here
            revenue: 0.98         // Slight revenue hit due to chaos
        }
    },
    {
        id: 'scotus_strike',
        type: 'negative',
        label: "⚖️ Supreme Court Strike",
        description: "Employer mandate ruled unconstitutional.",
        duration: 10,
        decayHalfLife: 100, // Effectively permanent/flat for 10 years until fix
        defaultSeverity: 1.0,
        impact: {
            revenue: 0.60,        // -40% employer revenue peak
            political_support: 0.40
        }
    },
    {
        id: 'cms_ransomware',
        type: 'negative',
        label: "💻 CMS Ransomware Attack",
        description: "State-sponsored cyberattack halts claims processing.",
        duration: 1,
        decayHalfLife: 0.5,   // Fast recovery
        defaultSeverity: 1.0,
        impact: {
            admin: 3.50,          // 350% cost spike
            revenue: 0.85         // Collection delay
        }
    },

    // --- POSITIVE BREAKTHROUGHS ---
    {
        id: 'ai_diagnostics',
        type: 'positive',
        label: "🤖 AI Diagnostics Breakthrough",
        description: "AI revolutionizes care, slashing diagnostic costs.",
        duration: 10,
        decayHalfLife: 100, // Permanent ramp impact (we invert decay logic for ramp? No, simplistic model: impact fades?
        // Actually breakthroughs usually GROW. 
        // The user spec said "Ramp-up: 10 years".
        // For simplicity in this engine, let's treat it as a constant benefit 
        // or we just define a long half-life so it stays active.
        // If we want it to GROW, we'd need a different curve. 
        // Let's assume the impact listed IS the peak or steady state.
        // User spec: 2030 Trigger: Costs decline 2030-39.
        // Let's use a flat impact for breakthroughs (high half life).
        defaultSeverity: 1.0,
        impact: {
            physician: 0.60,      // -40% 
            admin: 0.75,
            providerCapacity: 1.25
        }
    },
    {
        id: 'diabetes_cure',
        type: 'positive',
        label: "💊 Type 2 Diabetes Cure",
        description: "GLP-1 gene therapy functionally cures 90% of T2D.",
        duration: 10,
        decayHalfLife: 100,
        defaultSeverity: 1.0,
        impact: {
            hospital: 0.80,
            drugs: 0.85
        }
    },
    {
        id: 'immigration_reform',
        type: 'positive',
        label: "🌍 Immigration Reform",
        description: "Visa fix injects 50k doctors/nurses.",
        duration: 10,
        decayHalfLife: 100,
        defaultSeverity: 1.0,
        impact: {
            physician: 0.85,
            gdp_growth: 0.005     // +0.5%
        }
    },
    {
        id: 'economic_boom',
        type: 'positive',
        label: "💰 Economic Boom",
        description: "High growth period increases revenue.",
        duration: 3,
        decayHalfLife: 2.0,
        defaultSeverity: 1.0,
        impact: {
            revenue: 1.18,        // +18% revenue (Stronger than spending)
            hospital: 1.08,       // +8% spending (Income elasticity < 1.0)
            physician: 1.08,
            drugs: 1.08,
            admin: 1.08,
            gdp_growth: 0.04      // +4% GDP Growth
        }
    },
    {
        id: 'precision_medicine',
        type: 'positive',
        label: "🏥 Precision Medicine",
        description: "Targeted therapies reduce waste.",
        duration: 10,
        decayHalfLife: 100,
        defaultSeverity: 1.0,
        impact: {
            drugs: 0.90,
            hospital: 0.95
        }
    },
    {
        id: 'pain_breakthrough',
        type: 'positive',
        label: "🌿 Chronic Pain Breakthrough",
        description: "Non-opioid alternatives reduce addiction costs.",
        duration: 5,
        decayHalfLife: 3.0,
        defaultSeverity: 1.0,
        impact: {
            drugs: 0.90,
            hospital: 0.95
        }
    },
    {
        id: 'global_stagnation',
        type: 'negative',
        label: "📉 The Great Stagnation",
        description: "Zero growth decade. Revenue flat, costs rise.",
        duration: 10,
        decayHalfLife: 100, // Persistent
        defaultSeverity: 1.0,
        impact: {
            revenue: 0.90,        // -10% Revenue
            gdp_growth: -0.015    // -1.5% GDP Growth
        }
    }
];

/**
 * Calculate the multiplier for a specific event at a specific point in time
 */
function calculateScientificImpact(targetYear, eventDef, eventState) {
    const triggerYear = eventState.triggerYear || 2029;
    const severity = eventState.severity !== undefined ? eventState.severity : (eventDef.defaultSeverity || 1.0);


    // Time delta
    const yearsSinceTrigger = targetYear - triggerYear;

    // Not started yet
    if (yearsSinceTrigger < 0) return null;

    // Past duration
    if (yearsSinceTrigger > eventDef.duration) return null;

    // Peak Year (Year 0)
    // We start at full peak
    let timeFactor = 1.0;

    if (yearsSinceTrigger > 0) {
        // Exponential decay: N(t) = N0 * e^(-t/tau)
        // half_life = tau * ln(2) => tau = half_life / ln(2)
        // decay_rate lambda = ln(2) / half_life
        const lambda = 0.6931 / (eventDef.decayHalfLife || 1.0);
        timeFactor = Math.exp(-lambda * yearsSinceTrigger);
    }

    // Identify if this is a "Multiplicative" impact (like 1.45x) or "Additive" (like +0.02)
    // Actually, our spec uses Multipliers relative to 1.0 for everything except 'gdp_growth'
    // E.g. hospital: 1.45. The DELTA is 0.45.
    // We decay the DELTA.
    // So Effective = 1.0 + (Peak - 1.0) * timeFactor * severity

    // For values < 1.0 (e.g. 0.88 revenue), Peak is 0.88, Delta is -0.12.
    // Effective = 1.0 + (-0.12) * timeFactor * severity.

    return {
        timeFactor,
        severity,
        yearsSinceTrigger
    };
}

/**
 * Get aggregated multipliers for all events in a given year
 */
export function getEventMultipliers(year, config, eventsState) {
    // Start Neutral
    let m = {
        revenue: 1.0,
        hospital: 1.0,
        physician: 1.0,
        drugs: 1.0,
        admin: 1.0,
        gdp_growth: 0.0 // Additive
    };

    if (!eventsState) return m;

    SIMULATION_EVENTS.forEach(eventDef => {
        // Get state for this event
        let state = null;
        if (Array.isArray(eventsState)) {
            // Legacy array support
            if (eventsState.includes(eventDef.id)) {
                state = { enabled: true, triggerYear: 2029, severity: eventDef.defaultSeverity || 1.0 };
            }
        } else {
            // Object map
            state = eventsState[eventDef.id];
        }

        if (state && state.enabled) {
            const calculation = calculateScientificImpact(year, eventDef, state);

            if (calculation) {
                // Apply impacts
                const { timeFactor, severity } = calculation;

                // Iterate through specific impacts defined for this event
                Object.keys(eventDef.impact).forEach(key => {
                    const peakVal = eventDef.impact[key];

                    if (key === 'gdp_growth') {
                        // Additive
                        // impact = peak * timeFactor * severity
                        m.gdp_growth += (peakVal * timeFactor * severity);
                    } else {
                        // Multiplicative (Revenue, Hospital, etc)
                        // Delta from 1.0
                        const delta = peakVal - 1.0;
                        const effectiveDelta = delta * timeFactor * severity;

                        // Combine: We MULTIPLY the current multiplier by (1 + effectiveDelta)
                        // This allows impacts to compound (e.g. two events raising costs)
                        if (m[key] !== undefined) {
                            m[key] *= (1.0 + effectiveDelta);
                        } else {
                            // Initialize if not in default set
                            m[key] = 1.0 + effectiveDelta;
                        }
                    }
                });
            }
        }
    });

    return m;
}

/**
 * Helper for UI/Config (Legacy interface wrapper)
 */
export function applyEventsToConfig(year, config, eventsState) {
    // We primarily use getEventMultipliers now for the heavy lifting.
    // This function can remain to return activeEvents list for the log.
    const activeList = [];
    SIMULATION_EVENTS.forEach(def => {
        const state = (!Array.isArray(eventsState) && eventsState[def.id]) ? eventsState[def.id] : null;
        if (state && state.enabled) {
            // Check if active this year roughly
            if (year >= state.triggerYear && year <= (state.triggerYear + def.duration)) {
                activeList.push({ id: def.id, label: def.label });
            }
        }
    });

    return { effectiveConfig: config, activeEvents: activeList };
}
